(function (root, factory) {
  'use strict';
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.C1DeliveryServerCheckout = api;
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', api.install, { once: true });
    else api.install();
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var STORAGE_PREFIX = 'c1-invoice-checkout-v1:';
  var MESSAGES = {
    CHECKOUT_CANCELLED: 'Review your delivery details before continuing.',
    EMPTY_CART: 'Add an item to your cart before continuing.',
    CART_CHANGED: 'Your cart changed. Please review it and try again.',
    QUOTE_CHANGED: 'Your delivery details changed. Please check the updated total before continuing.',
    QUOTE_EXPIRED: 'Your shipping total expired. Please check the updated total before continuing.',
    CHECKOUT_BUSY: 'Checkout is already being prepared. Please wait.',
    CHECKOUT_NOT_STARTED: 'Please check the current delivery total before continuing.',
    CHECKOUT_REFRESHED: 'Please check the earlier payment with us before starting another checkout.',
    EXISTING_CHECKOUT_CHANGED: 'An earlier checkout uses different details. Please check its payment or COD status with us before continuing.',
    STORAGE_UNAVAILABLE: 'Please allow this site to use session storage, then try again.',
    CHECKOUT_HELD: 'An earlier checkout is still being checked. Please keep its payment tab and contact us before starting another.',
    CART_CLEANUP_PENDING: 'Your order is placed. We couldn’t refresh your cart yet. Please reload before shopping again.',
    ADDRESS_CHANGED: 'A payment page was already prepared with your earlier details. Please contact us before changing that checkout.',
    CHECKOUT_UNAVAILABLE: 'We couldn’t prepare checkout. Your cart and delivery details are still here. Please try again.'
  };
  function failure(code) { var error = new Error(MESSAGES[code] || MESSAGES.CHECKOUT_UNAVAILABLE); error.code = code; return error; }
  // Shop checkout can take over. This is not an earlier payment.
  function shopifyFallbackError() { var error = failure('CHECKOUT_UNAVAILABLE'); error.fallbackToShopify = true; return error; }
  function text(value) { return typeof value === 'string' ? value.trim() : ''; }
  function canonical(value) {
    if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
    if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(function (key) { return JSON.stringify(key) + ':' + canonical(value[key]); }).join(',') + '}';
    return JSON.stringify(value);
  }
  async function sha256(value) {
    if (!root.crypto || !root.crypto.subtle) throw failure('CHECKOUT_UNAVAILABLE');
    var bytes = await root.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(bytes), function (byte) { return byte.toString(16).padStart(2, '0'); }).join('');
  }
  function endpoint(value) {
    var url;
    try { url = new URL(value); } catch (_) { throw failure('CHECKOUT_UNAVAILABLE'); }
    if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) throw failure('CHECKOUT_UNAVAILABLE');
    return url.href.replace(/\/$/, '');
  }
  function safeInvoice(value, hosts) {
    var url;
    try { url = new URL(value); } catch (_) { throw failure('CHECKOUT_HELD'); }
    if (url.protocol !== 'https:' || url.username || url.password || url.hash || (url.port && url.port !== '443') || !hosts.includes(url.hostname)) throw failure('CHECKOUT_HELD');
    return url.href;
  }
  function money(value) { return Number.isSafeInteger(value) && value >= 0; }
  function normalizeBreakdown(value, amount) {
    if (!value || !money(value.fare) || !money(value.rushFee) || value.fare + value.rushFee !== amount) throw failure('CHECKOUT_UNAVAILABLE');
    return { fare: value.fare, rushFee: value.rushFee };
  }
  function normalizeTotals(value) {
    if (!value || value.currency !== 'PHP' || !['items', 'discount', 'shipping', 'tax', 'total'].every(function (key) { return money(value[key]); }) || typeof value.taxIncluded !== 'boolean') throw failure('CHECKOUT_UNAVAILABLE');
    if (value.items - value.discount + value.shipping + (value.taxIncluded ? 0 : value.tax) !== value.total) throw failure('CHECKOUT_UNAVAILABLE');
    return { currency: 'PHP', items: value.items, discount: value.discount, shipping: value.shipping, tax: value.tax, taxIncluded: value.taxIncluded, total: value.total };
  }
  function normalizeQuote(value) {
    if (!value || !text(value.quoteId) || !Array.isArray(value.rates) || !value.rates.length || value.rates.length > 50) throw failure('CHECKOUT_UNAVAILABLE');
    var seen = new Set();
    var result = { quoteId: value.quoteId, expiresAt: value.expiresAt, rates: value.rates.map(function (rate) {
      if (!rate || !text(rate.id) || seen.has(rate.id) || !text(rate.title) || rate.currency !== 'PHP' || !money(rate.amount)) throw failure('CHECKOUT_UNAVAILABLE');
      seen.add(rate.id);
      var totals = normalizeTotals(rate.totals);
      if (totals.shipping !== rate.amount) throw failure('CHECKOUT_UNAVAILABLE');
      var normalized = { id: rate.id, title: rate.title, amount: rate.amount, currency: rate.currency, totals: totals };
      if (rate.breakdown !== undefined) normalized.breakdown = normalizeBreakdown(rate.breakdown, rate.amount);
      return normalized;
    }) };
    if (value.defaultRateId !== undefined) {
      if (!text(value.defaultRateId) || !result.rates.some(function (rate) { return rate.id === value.defaultRateId; })) throw failure('CHECKOUT_UNAVAILABLE');
      result.defaultRateId = value.defaultRateId;
    }
    return result;
  }
  function formatMoney(amount) { return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount / 100); }
  function deliveryPayload(value) {
    value = value || {};
    var result = {};
    ['firstName', 'lastName', 'email', 'phone', 'streetAddress', 'zip', 'landmark'].forEach(function (key) { result[key] = text(value[key]); });
    ['province', 'city', 'barangay'].forEach(function (key) { result[key + 'Id'] = text(value[key] && value[key].id || value[key + 'Id']); });
    if (!['firstName', 'lastName', 'email', 'phone', 'streetAddress', 'zip', 'provinceId', 'cityId', 'barangayId'].every(function (key) { return result[key] && result[key].length <= 254 && !/[\0\r\n]/.test(result[key]); })) throw failure('CHECKOUT_UNAVAILABLE');
    if (!/^\d{4}$/.test(result.zip) || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(result.email) || result.landmark.length > 150 || /[\0\r\n]/.test(result.landmark)) throw failure('CHECKOUT_UNAVAILABLE');
    // Omit false to preserve the identity of existing standard-only checkouts.
    if (value.requestRush === true) {
      result.requestRush = true;
      var pin = value.dropoffPin;
      if (pin !== undefined) {
        if (!pin || pin.confirmed !== true || typeof pin.lat !== 'number' || typeof pin.lng !== 'number' ||
            !Number.isFinite(pin.lat) || !Number.isFinite(pin.lng) || pin.lat < 5 || pin.lat > 21.5 || pin.lng < 116 || pin.lng > 127) throw failure('CHECKOUT_UNAVAILABLE');
        result.dropoffPin = { lat: pin.lat, lng: pin.lng, confirmed: true };
      }
    }
    return result;
  }

  // The review exists only in the document; no address/contact values are stored.
  function confirmShipping(review, options) {
    options = options || {};
    var doc = options.document || root.document;
    if (!doc || !doc.body) return Promise.reject(failure('CHECKOUT_UNAVAILABLE'));
    return new Promise(function (resolve) {
      var previous = doc.activeElement, settled = false, id = 'c1-shipping-review-' + Math.random().toString(36).slice(2);
      function el(tag, className, value) { var node = doc.createElement(tag); if (className) node.className = className; if (value !== undefined) node.textContent = value; return node; }
      var dialog = el('dialog', 'c1-delivery-dialog'), heading = el('h2', 'c1-delivery__title', 'Review your delivery');
      heading.id = id; heading.tabIndex = -1;
      dialog.setAttribute('aria-labelledby', id); dialog.setAttribute('aria-modal', 'true');
      var header = el('div', 'c1-delivery__header'), close = el('button', 'c1-delivery__close', 'Back'); close.type = 'button';
      header.append(heading, close); dialog.append(header);
      dialog.append(el('p', 'c1-delivery__introduction', 'Check your details and shipping total. These details will be set on your payment page.'));
      var delivery = review.delivery, address = el('p', 'c1-delivery__introduction');
      [delivery.firstName + ' ' + delivery.lastName, delivery.email, delivery.phone, delivery.streetAddress,
        'Barangay: ' + (delivery.barangay && delivery.barangay.name || ''),
        [delivery.city && delivery.city.name, delivery.province && delivery.province.name, delivery.zip].filter(Boolean).join(', '),
        delivery.landmark ? 'Landmark: ' + delivery.landmark : ''].filter(Boolean).forEach(function (line, index) {
          if (index) address.append(doc.createElement('br')); address.append(doc.createTextNode(line));
        });
      dialog.append(address);
      var form = el('form'), field = el('div', 'c1-delivery__field c1-delivery__field--full'), label = el('label', '', 'Shipping option'), select = el('select');
      select.id = id + '-rate'; label.htmlFor = select.id;
      review.rates.forEach(function (rate) { var option = el('option', '', rate.title + ' · ' + formatMoney(rate.amount)); option.value = rate.id; select.append(option); });
      field.append(label, select); form.append(field);
      var summary = el('div', 'c1-delivery__introduction'); summary.setAttribute('aria-live', 'polite'); summary.setAttribute('aria-atomic', 'true'); form.append(summary);
      function showTotal() {
        var selected = review.rates.find(function (rate) { return rate.id === select.value; }), totals = selected.totals;
        summary.replaceChildren();
        var rows = [['Items', totals.items], ['Shipping', totals.shipping]];
        if (selected.breakdown) rows.push(['Lalamove fare', selected.breakdown.fare], ['Rush fee', selected.breakdown.rushFee]);
        if (totals.discount) rows.push(['Discount', -totals.discount]);
        if (totals.tax) rows.push([totals.taxIncluded ? 'Tax included' : 'Tax', totals.tax]);
        rows.push(['Total', totals.total]);
        rows.forEach(function (row) { var line = el(row[0] === 'Total' ? 'strong' : 'p', '', row[0] + ': ' + formatMoney(row[1])); summary.append(line); });
      }
      select.addEventListener('change', showTotal); showTotal();
      var actions = el('div', 'c1-delivery__actions'), submit = el('button', 'c1-delivery__continue', 'Continue to payment'), back = el('button', 'c1-delivery__back', 'Back to delivery details');
      submit.type = 'submit'; back.type = 'button'; actions.append(submit, back); form.append(actions); dialog.append(form);
      function finish(value) { if (settled) return; settled = true; if (typeof dialog.close === 'function') dialog.close(); dialog.remove(); if (previous && previous.isConnected && previous.focus) previous.focus(); resolve(value); }
      close.addEventListener('click', function () { finish(null); }); back.addEventListener('click', function () { finish(null); });
      dialog.addEventListener('cancel', function (event) { event.preventDefault(); finish(null); });
      form.addEventListener('submit', function (event) { event.preventDefault(); finish(select.value); });
      dialog.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') { event.preventDefault(); finish(null); }
        if (event.key !== 'Tab') return;
        var first = close, last = back;
        if (event.shiftKey && (doc.activeElement === first || doc.activeElement === heading)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
      });
      doc.body.append(dialog); if (typeof dialog.showModal === 'function') dialog.showModal(); else { dialog.setAttribute('open', ''); dialog.setAttribute('role', 'dialog'); }
      heading.focus();
    });
  }

  function createCheckoutHandler(options) {
    options = options || {};
    var config = options.config || {}, base = endpoint(config.apiBase), fetcher = options.fetch || root.fetch && root.fetch.bind(root);
    var storage = options.storage || root.sessionStorage, hash = options.hash || sha256, now = options.now || Date.now;
    // Only legacy callers which explicitly inject a review callback use it.
    // The storefront shows its quote in the editable delivery form instead.
    var confirm = options.confirmShipping, hosts = config.invoiceHosts || [], inProgress = false, cachedQuote = null, warmingSession = null;
    var storageKey = STORAGE_PREFIX + base, uuid = options.uuid || function () { return root.crypto.randomUUID(); };
    if (!fetcher || !Array.isArray(hosts) || !hosts.length) throw failure('CHECKOUT_UNAVAILABLE');
    function load() {
      try { var value = storage.getItem(storageKey); if (!value) return null; var parsed = JSON.parse(value); if (parsed.version !== 1 || !text(parsed.token) || !text(parsed.cartDigest)) throw new Error(); return parsed; }
      catch (_) { throw failure('STORAGE_UNAVAILABLE'); }
    }
    function save(value) {
      try { storage.setItem(storageKey, JSON.stringify(value)); if (storage.getItem(storageKey) !== JSON.stringify(value)) throw new Error(); }
      catch (_) { throw failure('STORAGE_UNAVAILABLE'); }
    }
    function routesRoot() { var value = config.routesRoot || config.routes && config.routes.root || '/'; if (typeof value !== 'string' || value[0] !== '/' || value.startsWith('//') || /[?#\\]/.test(value)) throw failure('CHECKOUT_UNAVAILABLE'); return value.replace(/\/$/, '') + '/'; }
    async function cart(allowEmpty) {
      var response = await fetcher(routesRoot() + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw failure('CHECKOUT_UNAVAILABLE'); var value = await response.json();
      if (!value || !text(value.token) || !Array.isArray(value.items)) throw failure('CHECKOUT_UNAVAILABLE');
      if (!allowEmpty && !value.items.length) throw failure('EMPTY_CART');
      return value;
    }
    async function post(path, body, token) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = controller && setTimeout(function () { controller.abort(); }, options.timeoutMs || 45000);
      try {
        var headers = { 'Content-Type': 'application/json', Accept: 'application/json' }; if (token) headers.Authorization = 'Bearer ' + token;
        // No cache mode: browsers never reuse POST responses (the server also
        // sends no-store), but Chrome skips its CORS preflight cache for
        // cache:'no-store' requests and would add one OPTIONS round trip each.
        var response = await fetcher(base + path, { method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', headers: headers, body: JSON.stringify(body), signal: controller && controller.signal });
        var value;
        // A non-JSON body (checkout switched off, or a proxy error page) did not
        // describe a payment. Same for a dropped connection or the abort below.
        try { value = await response.json(); } catch (_) { throw shopifyFallbackError(); }
        if (!response.ok) {
          var serverCode = value && value.error && value.error.code;
          var codes = { RECONCILIATION_REQUIRED: 'CHECKOUT_HELD', DRAFT_MISMATCH: 'CHECKOUT_HELD', EXISTING_CHECKOUT_CHANGED: 'EXISTING_CHECKOUT_CHANGED', ACTIVE_DRAFT_EXISTS: 'EXISTING_CHECKOUT_CHANGED', NO_CHECKOUT_ATTEMPT: 'CHECKOUT_HELD', CHECKOUT_NOT_STARTED: 'CHECKOUT_NOT_STARTED', CART_CHANGED: 'CART_CHANGED', QUOTE_CHANGED: 'QUOTE_CHANGED', QUOTE_EXPIRED: 'QUOTE_EXPIRED' };
          var mapped = codes[serverCode] || 'CHECKOUT_UNAVAILABLE';
          var error = failure(mapped); error.status = response.status; error.serverCode = serverCode;
          error.safeToRetry = Boolean(value && value.error && value.error.safeToRetry === true);
          // 503 CHECKOUT_UNAVAILABLE, checkout switched off, and any other
          // rejection that does not name an earlier payment.
          if (mapped === 'CHECKOUT_UNAVAILABLE') error.fallbackToShopify = true;
          throw error;
        }
        return value;
      } catch (error) { if (error && error.code) throw error; throw shopifyFallbackError(); }
      finally { if (timer) clearTimeout(timer); }
    }
    // The browser caches a CORS preflight per URL. One marked request per route
    // while the shopper types moves those OPTIONS round trips off the shipping
    // and payment path. It carries no session, cart or delivery data; the server
    // answers {warm:true} before reading any (older servers reject it as 401).
    var warmedAt = -Infinity;
    function warmPreflights() {
      if (now() - warmedAt < 240000) return;
      warmedAt = now();
      ['/quote', '/prepare'].forEach(function (path) {
        try {
          Promise.resolve(fetcher(base + path, { method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: 'Bearer warm' }, body: '{"warm":true}' })).catch(function () {});
        } catch (_) {}
      });
    }
    async function newSession(reference, digest, previous) {
      var result = await post('/session', { cartReference: reference });
      if (!text(result.token) || /[\r\n]/.test(result.token) || !Number.isFinite(Date.parse(result.expiresAt)) || Date.parse(result.expiresAt) <= now()) throw failure('CHECKOUT_UNAVAILABLE');
      var value = { version: 1, token: result.token, expiresAt: result.expiresAt, cartReference: reference, cartDigest: digest, active: previous && previous.active || null };
      if (previous && Array.isArray(previous.history)) value.history = previous.history;
      save(value);
      return value;
    }
    function notStarted(error) { return error && error.serverCode === 'CHECKOUT_NOT_STARTED' && error.safeToRetry === true; }
    // A named status hold means an invoice may already exist. Stamp it before
    // any later 503, which must not delete that identity or open shop checkout.
    var namedHolds = { RECONCILIATION_REQUIRED: true, DRAFT_MISMATCH: true, EXISTING_CHECKOUT_CHANGED: true, ACTIVE_DRAFT_EXISTS: true, NO_CHECKOUT_ATTEMPT: true };
    function mightHaveInvoice(active) {
      return !!(active && (active.phase === 'prepared' || active.unresolved === true || namedHolds[active.serverHold]));
    }
    function rememberNamedHold(state, error) {
      if (!state || !state.active || !error || !namedHolds[error.serverCode]) return false;
      state.active.phase = 'unknown';
      state.active.unresolved = true;
      state.active.serverHold = error.serverCode;
      save(state);
      return true;
    }
    // Shop checkout is safe only when this browser has no row that might already
    // be an invoice. A preparing row was saved before /prepare returned.
    function shopifyFallbackSafe() {
      var state;
      try { state = load(); } catch (_) { return false; }
      if (!state || !state.active) return true;
      if (mightHaveInvoice(state.active) || state.active.phase === 'preparing') return false;
      return true;
    }
    function statusBody(active) { return { idempotencyKey: active.idempotencyKey, continueWithCurrentDetails: true }; }
    function archiveActive(state, reason, checkoutUrl) {
      var history = Array.isArray(state && state.history) ? state.history.slice() : [];
      if (state && state.active) {
        var entry = { cartReference: state.cartReference || null, cartDigest: state.cartDigest,
          active: state.active, reason: reason };
        if (checkoutUrl) entry.checkoutUrl = checkoutUrl;
        // Never silently cap or discard an earlier payment's recovery identity.
        history.push(entry);
      }
      return history;
    }
    function archivedCart(state, digest) {
      var history = state && Array.isArray(state.history) ? state.history : [];
      for (var i = history.length - 1; i >= 0; i--) {
        if (history[i].cartDigest === digest && history[i].reason === 'cart-changed') return history[i].terminal ? null : history[i].active;
      }
      return null;
    }
    function retireHistory(state, outcome) {
      if (!state.active || !Array.isArray(state.history)) return;
      state.history = state.history.map(function (entry) {
        return entry.cartDigest === state.cartDigest && entry.active && entry.active.idempotencyKey === state.active.idempotencyKey
          ? Object.assign({}, entry, { terminal: outcome }) : entry;
      });
    }
    function verifyResult(result, active) {
      var totals = normalizeTotals(result && result.totals);
      if (!active || canonical(totals) !== canonical(active.totals)) throw failure('CHECKOUT_HELD');
      return { checkoutUrl: safeInvoice(result.checkoutUrl, hosts) };
    }
    async function completedResult(result, state) {
      if (result.completed !== true || result.orderCreated !== true || !/^gid:\/\/shopify\/Order\/\d+$/.test(result.orderId || '')) throw failure('CHECKOUT_HELD');
      var totals = normalizeTotals(result.totals);
      if (canonical(totals) !== canonical(state.active.totals)) throw failure('CHECKOUT_HELD');
      var statusUrl = safeInvoice(result.orderStatusUrl, config.orderStatusHosts || hosts);
      // Only clear the cart which produced this completed order. A later or
      // edited cart belongs to the shopper and must survive order recovery.
      var current = await cart(true), currentDigest = await hash(canonical(current));
      if (await hash('gid://shopify/Cart/' + current.token) === state.cartDigest && currentDigest === state.active.cartContentDigest) {
        try {
          var cleared = await fetcher(routesRoot() + 'cart/clear.js', { method: 'POST', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: '{}' });
          if (!cleared.ok || (await cart(true)).items.length) throw failure('CART_CLEANUP_PENDING');
        } catch (_) { throw failure('CART_CLEANUP_PENDING'); }
      }
      retireHistory(state, 'completed'); state.active = null; save(state);
      return { checkoutUrl: statusUrl, orderCreated: true, orderId: result.orderId };
    }
    async function quoteFor(delivery, snapshot) {
      var state = snapshot ? snapshot.state : load(), early = null;
      // A stored unexpired session with no earlier payment attempt already names
      // its cart. Send the quote while the browser cart is read; that read still
      // decides below whether the answer belongs to this cart or is discarded.
      if (!snapshot && state && !state.active && Date.parse(state.expiresAt) > now() + 30000) {
        early = { token: state.token, cartDigest: state.cartDigest, request: post('/quote', { delivery: deliveryPayload(delivery) }, state.token) };
        early.request.catch(function () {});
      }
      if (warmingSession && !early) { await warmingSession; if (!snapshot) state = load(); }
      var original = snapshot ? snapshot.original : await cart(Boolean(state && state.active));
      var reference = 'gid://shopify/Cart/' + original.token;
      var digest = await hash(reference), cartContentDigest = await hash(canonical(original));
      if (state && state.cartDigest !== digest) {
        var history = archiveActive(state, 'cart-changed'), returningActive = archivedCart(state, digest);
        state = await newSession(reference, digest, { active: returningActive, history: history });
      }
      // An earlier attempt must be reconciled by the payment action. Previewing
      // its recorded total must never create another draft or clear a cart.
      if (state && state.active) {
        var recoveryReference = state.cartReference || (state.cartDigest === digest ? reference : null), recoveredStatus;
        try {
          if (Date.parse(state.expiresAt) <= now()) {
            if (!recoveryReference) throw failure('CHECKOUT_HELD');
            state = await newSession(recoveryReference, state.cartDigest, state);
          }
          try { recoveredStatus = await post('/status', statusBody(state.active), state.token); }
          catch (error) {
            if (error.status !== 401 || error.serverCode !== 'SESSION_EXPIRED' || !recoveryReference) throw error;
            state = await newSession(recoveryReference, state.cartDigest, state);
            recoveredStatus = await post('/status', statusBody(state.active), state.token);
          }
        } catch (error) {
          if (notStarted(error)) {
            retireHistory(state, 'not-started'); state.active = null; save(state);
          } else if (rememberNamedHold(state, error) || mightHaveInvoice(state && state.active)) {
            // Reconciled hold, prepared invoice, or a status hold just stamped.
            // A later 503 must keep this identity and must not fall back.
            throw failure('CHECKOUT_HELD');
          } else if (state.active && state.active.phase === 'preparing' && error && error.fallbackToShopify === true) {
            // A preparing row never received an invoice. Checkout is off, or
            // the request never described a payment.
            retireHistory(state, 'unavailable'); state.active = null; save(state); throw error;
          } else if (state.active && state.active.phase === 'unknown' && error && error.fallbackToShopify === true) {
            retireHistory(state, 'unavailable'); state.active = null; save(state); throw error;
          } else throw failure('CHECKOUT_HELD');
        }
        if (state.active) {
          var totals = normalizeTotals(state.active.totals), completed = recoveredStatus && recoveredStatus.orderCreated === true;
          if (completed) {
            if (recoveredStatus.completed !== true || !/^gid:\/\/shopify\/Order\/\d+$/.test(recoveredStatus.orderId || '') || canonical(normalizeTotals(recoveredStatus.totals)) !== canonical(totals)) throw failure('CHECKOUT_HELD');
            safeInvoice(recoveredStatus.orderStatusUrl, config.orderStatusHosts || hosts);
          } else verifyResult(recoveredStatus, state.active);
          var unchanged = completed || state.cartDigest === digest && state.active.cartContentDigest === cartContentDigest && state.active.deliveryDigest === await hash(canonical(deliveryPayload(delivery)));
          if (unchanged) {
            cachedQuote = null;
            return { quoteId: state.active.quoteId, expiresAt: null, recoverable: true,
              rates: [{ id: state.active.selectedRateId, title: state.active.rateTitle || 'Selected delivery',
                amount: totals.shipping, currency: 'PHP', totals: totals }] };
          }
          // A verified ready earlier invoice remains intact. Quote the current
          // reviewed snapshot, and reserve its new identity only on Continue.
          state.active.phase = 'prepared'; save(state);
        }
      }
      var payload = deliveryPayload(delivery), deliveryDigest = await hash(canonical(payload)), quoteRequest = null;
      if (early && early.cartDigest === digest) quoteRequest = early.request;
      else if (early) {
        // A different browser cart: ignore the early answer and take the
        // ordinary path, after any warm-up which may be opening its session.
        if (warmingSession) await warmingSession;
        state = load();
      }
      if (!quoteRequest && (!state || state.cartDigest !== digest || Date.parse(state.expiresAt) <= now())) state = await newSession(reference, digest, state);
      var quoteResponse;
      try { quoteResponse = await (quoteRequest || post('/quote', { delivery: payload }, state.token)); }
      catch (error) { if (error.status !== 401 || error.serverCode !== 'SESSION_EXPIRED') throw error; state = await newSession(reference, digest, state); quoteResponse = await post('/quote', { delivery: payload }, state.token); }
      var quote = normalizeQuote(quoteResponse);
      if (!Number.isFinite(Date.parse(quote.expiresAt)) || Date.parse(quote.expiresAt) <= now()) throw failure('CHECKOUT_UNAVAILABLE');
      cachedQuote = { quote: quote, cartDigest: digest, cartContentDigest: cartContentDigest,
        deliveryDigest: deliveryDigest, sessionToken: state.token,
        priorActiveId: state.active && state.active.idempotencyKey || null };
      warmPreflights();
      // Keep the trusted quote private; form code receives a detached display copy.
      return JSON.parse(JSON.stringify(quote));
    }
    async function prepareCheckout(delivery, selection) {
      if (inProgress) throw failure('CHECKOUT_BUSY'); inProgress = true;
      var state;
      try {
        if (warmingSession) await warmingSession;
        state = load();
        var original = await cart(Boolean(state && state.active)), reference = 'gid://shopify/Cart/' + original.token;
        var digest = await hash(reference), cartContentDigest = await hash(canonical(original)), payload, deliveryDigest;
        if (state && state.cartDigest !== digest) {
          var history = archiveActive(state, 'cart-changed'), returningActive = archivedCart(state, digest);
          state = await newSession(reference, digest, { active: returningActive, history: history });
        }
        var priorReady = null;
        if (state && state.active) {
          var recoveryReference = state.cartReference || (state.cartDigest === digest ? reference : null), statusResult;
          try {
            if (Date.parse(state.expiresAt) <= now()) {
              if (!recoveryReference) throw failure('CHECKOUT_HELD');
              state = await newSession(recoveryReference, state.cartDigest, state);
            }
            try { statusResult = await post('/status', statusBody(state.active), state.token); }
            catch (error) {
              if (error.status !== 401 || error.serverCode !== 'SESSION_EXPIRED' || !recoveryReference) throw error;
              state = await newSession(recoveryReference, state.cartDigest, state);
              statusResult = await post('/status', statusBody(state.active), state.token);
            }
          } catch (error) {
            if (notStarted(error)) {
              retireHistory(state, 'not-started'); state.active = null; save(state); cachedQuote = null;
              throw failure('CHECKOUT_NOT_STARTED');
            }
            if (rememberNamedHold(state, error) || mightHaveInvoice(state && state.active)) {
              throw failure('CHECKOUT_HELD');
            }
            // A preparing row whose status never named a payment, or an unknown
            // row that was never reconciled, is not an earlier payment.
            if (state.active && (state.active.phase === 'preparing' || state.active.phase === 'unknown') && error && error.fallbackToShopify === true) {
              retireHistory(state, 'unavailable'); state.active = null; save(state); cachedQuote = null;
              throw error;
            }
            throw failure('CHECKOUT_HELD');
          }
          if (statusResult && statusResult.orderCreated === true) return await completedResult(statusResult, state);
          payload = deliveryPayload(delivery); deliveryDigest = await hash(canonical(payload));
          var recovered = verifyResult(statusResult, state.active);
          var sameSelection = !selection || selection.quoteId === state.active.quoteId && selection.selectedRateId === state.active.selectedRateId;
          if (state.active.cartContentDigest === cartContentDigest && state.active.deliveryDigest === deliveryDigest && sameSelection) {
            state.active.phase = 'prepared'; save(state); return recovered;
          }
          priorReady = { idempotencyKey: state.active.idempotencyKey, checkoutUrl: recovered.checkoutUrl };
        }
        payload = deliveryPayload(delivery); deliveryDigest = await hash(canonical(payload));
        var quote, rateId;
        if (selection) {
          if (!cachedQuote || !state || selection.quoteId !== cachedQuote.quote.quoteId || cachedQuote.sessionToken !== state.token) throw failure('QUOTE_CHANGED');
          if ((cachedQuote.priorActiveId || null) !== (state.active && state.active.idempotencyKey || null)) throw failure('QUOTE_CHANGED');
          if (!Number.isFinite(Date.parse(state.expiresAt)) || Date.parse(state.expiresAt) <= now()) { cachedQuote = null; throw failure('QUOTE_EXPIRED'); }
          if (cachedQuote.cartDigest !== digest || cachedQuote.cartContentDigest !== cartContentDigest) { cachedQuote = null; throw failure('CART_CHANGED'); }
          if (cachedQuote.deliveryDigest !== deliveryDigest) { cachedQuote = null; throw failure('QUOTE_CHANGED'); }
          quote = cachedQuote.quote; rateId = selection.selectedRateId;
        } else if (typeof confirm === 'function') {
          await quoteFor(delivery, { state: state, original: original });
          state = load(); quote = cachedQuote.quote;
          rateId = await confirm({ delivery: delivery, rates: quote.rates, quoteId: quote.quoteId });
        } else throw failure('QUOTE_CHANGED');
        if (!rateId) throw failure(selection ? 'QUOTE_CHANGED' : 'CHECKOUT_CANCELLED');
        var rate = quote.rates.find(function (item) { return item.id === rateId; });
        if (!rate) { cachedQuote = null; throw failure('QUOTE_CHANGED'); }
        if (Date.parse(quote.expiresAt) <= now()) { cachedQuote = null; throw failure('QUOTE_EXPIRED'); }
        // Inline selection already checked a fresh cart against its bound quote
        // above; /prepare independently reads it twice before any mutation.
        // Legacy review callbacks can wait for user input, so re-read for those.
        if (!selection) {
          var latest = await cart();
          if (canonical(original) !== canonical(latest)) throw failure('CART_CHANGED');
        }
        if (state.active) {
          if (!priorReady || priorReady.idempotencyKey !== state.active.idempotencyKey) throw failure('CHECKOUT_HELD');
          state.history = archiveActive(state, 'new-snapshot', priorReady.checkoutUrl);
        }
        state.active = { phase: 'preparing', idempotencyKey: uuid(), quoteId: quote.quoteId, selectedRateId: rate.id, rateTitle: rate.title, deliveryDigest: deliveryDigest, cartContentDigest: cartContentDigest, totals: rate.totals };
        save(state); // Commit the single-create identity before any provider work.
        cachedQuote = null;
        var result;
        try {
          var prepareBody = { idempotencyKey: state.active.idempotencyKey, quoteId: quote.quoteId, selectedRateId: rate.id, delivery: payload, continueWithCurrentDetails: true };
          var prepared = await post('/prepare', prepareBody, state.token);
          if (prepared && prepared.orderCreated === true) return await completedResult(prepared, state);
          result = verifyResult(prepared, state.active);
          state.active.phase = 'prepared'; save(state); return result;
        } catch (error) {
          // Only these fresh prepare rejections have durable pre-create proof
          // in transport.withOutcome (rejected, no draft, no active checkout).
          // A missing attempt or conflict may refer to an earlier payment, so
          // safeToRetry alone must never discard its local recovery identity.
          if (error.safeToRetry === true && ['QUOTE_EXPIRED', 'QUOTE_CHANGED', 'CART_CHANGED'].includes(error.serverCode)) {
            state.active = null; save(state); throw error;
          }
          // This attempt saved phase 'preparing' and never received an invoice.
          // A 503, a switched-off checkout, or no response must not become a
          // payment hold and must not keep that speculative identity.
          if (state.active && state.active.phase === 'preparing' && !priorReady && error && error.fallbackToShopify === true) {
            state.active = null; save(state);
            var fallback = shopifyFallbackError();
            if (error.status) fallback.status = error.status;
            if (error.serverCode) fallback.serverCode = error.serverCode;
            throw fallback;
          }
          if (state.active) {
            state.active.phase = 'unknown';
            state.active.unresolved = true;
            if (error && error.serverCode) state.active.serverHold = error.serverCode;
            save(state);
          }
          throw failure(error.code === 'CART_CLEANUP_PENDING' ? 'CART_CLEANUP_PENDING' : 'CHECKOUT_HELD');
        }
      } finally { inProgress = false; }
    }
    prepareCheckout.quote = async function (delivery) {
      if (inProgress) throw failure('CHECKOUT_BUSY');
      inProgress = true; cachedQuote = null;
      try { return await quoteFor(delivery); }
      finally { inProgress = false; }
    };
    prepareCheckout.shopifyFallbackSafe = shopifyFallbackSafe;
    prepareCheckout.warmup = function () {
      if (warmingSession) return warmingSession;
      if (inProgress) return Promise.resolve(false);
      warmingSession = Promise.resolve().then(async function () {
        var state = load();
        // Leave any earlier payment identity untouched until explicit recovery.
        if (state && state.active) return false;
        var original = await cart(false), reference = 'gid://shopify/Cart/' + original.token;
        var digest = await hash(reference);
        if (state && state.cartDigest !== digest && archivedCart(state, digest)) return false;
        if (!(state && state.cartDigest === digest && Number.isFinite(Date.parse(state.expiresAt)) && Date.parse(state.expiresAt) > now())) await newSession(reference, digest, state);
        warmPreflights();
        return true;
      }).catch(function () {
        // A warm-up failure is non-blocking; the normal quote path retries and
        // exposes any actual error once the shopper requests shipping.
        return false;
      }).finally(function () { warmingSession = null; });
      return warmingSession;
    };
    return prepareCheckout;
  }
  function install() {
    if (!root.C1Delivery || typeof root.C1Delivery.setCheckoutHandler !== 'function' || !root.document) return false;
    var node = root.document.getElementById('c1-delivery-config'), config;
    try { config = JSON.parse(node && node.textContent || '{}'); if (!config.apiBase) return false; root.C1Delivery.setCheckoutHandler(createCheckoutHandler({ config: config })); return true; }
    catch (_) { return false; }
  }
  return { createCheckoutHandler: createCheckoutHandler, confirmShipping: confirmShipping, normalizeQuote: normalizeQuote, deliveryPayload: deliveryPayload, install: install };
});
